import { useState } from 'react'
import { Controller } from 'react-hook-form'
import { CKEditor4 } from '@/components/editor/CKEditor4'
import { cn } from '@lib/utils/helpers'
import { InviteAcknowledgementEditor } from './InviteAcknowledgementEditor'
import type { OrganizationEditSectionProps } from '../../types/components'
import type { OrganizationFormInput } from '../../types/schemas'

type InviteField = keyof Pick<
  OrganizationFormInput,
  'invite_intro_html' | 'invite_welcome_html' | 'invite_fees_html' | 'invite_eligibility_html'
>

type SimpleSection = {
  id: string
  name: InviteField
  tabLabel: string
  title: string
  description: string
  editorId: string
}

type AckStep = {
  name: InviteField
  title: string
  step: string
  editorId: string
  kind: 'fee' | 'eligibility'
}

const WELCOME_SECTION: SimpleSection = {
  id: 'welcome',
  name: 'invite_welcome_html',
  tabLabel: 'Welcome',
  title: 'Welcome',
  description: 'Shown first when an applicant opens an invite link.',
  editorId: 'org-invite-welcome',
}

const INSTRUCTIONS_SECTION: SimpleSection = {
  id: 'instructions',
  name: 'invite_intro_html',
  tabLabel: 'Invite instructions',
  title: 'Invite instructions',
  description: 'Shown on the registration form after invitation acceptance.',
  editorId: 'org-invite-intro',
}

/** Fees and eligibility are one applicant screen: step 1, then step 2. */
const FEES_TAB_ID = 'fees'

const ACK_STEPS: AckStep[] = [
  {
    name: 'invite_fees_html',
    title: 'Fees and Obligations',
    step: 'Step 1 of 2',
    editorId: 'org-invite-fees',
    kind: 'fee',
  },
  {
    name: 'invite_eligibility_html',
    title: 'Program Eligibility',
    step: 'Step 2 of 2',
    editorId: 'org-invite-eligibility',
    kind: 'eligibility',
  },
]

const TABS = [
  { id: WELCOME_SECTION.id, tabLabel: WELCOME_SECTION.tabLabel },
  { id: FEES_TAB_ID, tabLabel: 'Fees and Eligibility' },
  { id: INSTRUCTIONS_SECTION.id, tabLabel: INSTRUCTIONS_SECTION.tabLabel },
]

export function OrganizationInvitePageTab({ ctx }: OrganizationEditSectionProps) {
  const [activeId, setActiveId] = useState(TABS[0].id)

  if (ctx.orgId !== 1) return null

  return (
    <div
      id="org-panel-invitepage"
      role="tabpanel"
      aria-labelledby="org-tab-invitepage"
      hidden={ctx.selectedTab !== 'invitepage'}
    >
      {ctx.selectedTab === 'invitepage' && (
        <div className="-m-4 w-[calc(100%+2rem)] bg-slate-50 p-4 sm:-m-5 sm:w-[calc(100%+2.5rem)] sm:p-5">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-slate-900">Invite page</h2>
            <p className="mt-1 max-w-3xl text-sm text-slate-500">
              What applicants read and accept before they start an application.
            </p>
          </div>

          <div className="mb-4 flex flex-wrap gap-2" role="tablist" aria-label="Invite page sections">
            {TABS.map((tab, index) => {
              const selected = tab.id === activeId
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  id={`invite-section-tab-${tab.id}`}
                  aria-selected={selected}
                  aria-controls={`invite-section-panel-${tab.id}`}
                  className={cn(
                    'inline-flex items-center gap-2 rounded-full border bg-white px-3 py-1.5 text-sm font-medium text-slate-600',
                    selected
                      ? 'border-violet-500 text-violet-700 shadow-sm'
                      : 'border-slate-200 hover:border-slate-300',
                  )}
                  onClick={() => setActiveId(tab.id)}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold',
                      selected ? 'bg-violet-600 text-white' : 'bg-slate-100 text-slate-500',
                    )}
                  >
                    {index + 1}
                  </span>
                  {tab.tabLabel}
                </button>
              )
            })}
          </div>

          <SimpleSectionPanel section={WELCOME_SECTION} active={activeId === WELCOME_SECTION.id} ctx={ctx} />

          {activeId === FEES_TAB_ID ? (
            <section
              id={`invite-section-panel-${FEES_TAB_ID}`}
              role="tabpanel"
              aria-labelledby={`invite-section-tab-${FEES_TAB_ID}`}
              className="w-full rounded-2xl border-2 border-violet-300 bg-white p-4 sm:p-6"
            >
              {ACK_STEPS.map((step, index) => (
                <Controller
                  key={step.name}
                  name={step.name}
                  control={ctx.control}
                  shouldUnregister={false}
                  render={({ field: { value, onChange } }) => (
                    <div className={cn(index > 0 && 'mt-8 border-t border-slate-200 pt-8')}>
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-violet-600">
                        {step.step}
                      </p>
                      <h2 className="text-lg font-semibold text-slate-900">{step.title}</h2>
                      <div className="mt-5 [&_.ck-editor__editable]:!min-h-[180px]">
                        <InviteAcknowledgementEditor
                          id={step.editorId}
                          kind={step.kind}
                          value={value || ''}
                          onChange={onChange}
                        />
                      </div>
                    </div>
                  )}
                />
              ))}
            </section>
          ) : (
            ACK_STEPS.map((step) => (
              <Controller
                key={step.name}
                name={step.name}
                control={ctx.control}
                shouldUnregister={false}
                render={() => <span className="hidden" />}
              />
            ))
          )}

          <SimpleSectionPanel
            section={INSTRUCTIONS_SECTION}
            active={activeId === INSTRUCTIONS_SECTION.id}
            ctx={ctx}
          />
        </div>
      )}
    </div>
  )
}

function SimpleSectionPanel({
  section,
  active,
  ctx,
}: {
  section: SimpleSection
  active: boolean
  ctx: OrganizationEditSectionProps['ctx']
}) {
  return (
    <Controller
      name={section.name}
      control={ctx.control}
      shouldUnregister={false}
      render={({ field: { value, onChange } }) =>
        active ? (
          <section
            id={`invite-section-panel-${section.id}`}
            role="tabpanel"
            aria-labelledby={`invite-section-tab-${section.id}`}
            className="w-full rounded-2xl border-2 border-violet-300 bg-white p-4 sm:p-6"
          >
            <h2 className="text-lg font-semibold text-slate-900">{section.title}</h2>
            <p className="mt-1 text-sm text-slate-500">{section.description}</p>
            <div className="mt-5 [&_.ck-editor__editable]:!min-h-[180px]">
              <div>
                <p className="mb-1.5 text-sm font-medium text-slate-700">Body text</p>
                <CKEditor4 id={section.editorId} value={value || ''} onChange={onChange} />
              </div>
            </div>
          </section>
        ) : (
          <span className="hidden" />
        )
      }
    />
  )
}
